/**
 * 適職診断の結果の「あなたのジグザグ学習の始め方」に出す 3 つの段。
 *
 * ジグザグ学習は、基礎・実践・本物を行き来して一段ずつ進む、ゆめさくの学び方 (2026-10-18 に代表が決めた)。
 * 言葉と順番は /women の HP の ZigzagMethod (apps/shegate-web/app/components/ZigzagMethod.tsx、2026-10-19 の案 A1) に合わせ、
 * 基礎で分かる、実践で手を動かす、本物で使う、の 3 段を上から下へ進み、次のレベルの基礎へ戻る。
 * 中身はその職種の本物の物だけで書く。60秒の仕事体験は結果の 04「今日の60秒」の札に置き、ここには並べない。
 * - 基礎で分かる  学習パスの「まず触る章」の 1 つ目 (job-diagnosis-path.generated.ts の名前と項目の数)
 * - 実践で手を動かす  受講生に公開中の道場か実践プロジェクトから 1 つ (ROLE_PRACTICE)
 * - 本物で使う  実プロダクト開発演習をレベル1 から。別途オプション料金
 * 何か月で届くかは約束しない。期間の言葉 (実践プロジェクトの duration など) は出さない。
 */
import type { RoleId } from "./job-type-diagnosis";
import { rolePathSummary, shortChapterName } from "./job-diagnosis-path";

export type ZigzagLane = "base" | "prac" | "real";

export const ZIGZAG_LANE_NAME: Record<ZigzagLane, string> = { base: "基礎", prac: "実践", real: "本物" };

/**
 * 職種ごとの実践の 1 本。道場は course-slugs.generated.ts の公開中のコースの題を「名前 副題」に分けて書く
 * (試験で題と突き合わせる)。クラウドエディターだけの道場 (dojo-git など) は管理者と講師にしか見えないので選ばない。
 * 実践プロジェクトは practical-projects/data.ts の id で書き、名前は HP の一覧 (PracticalProjects.tsx) の短い題にする。
 * 合う物が無い職種は general にして、作り話で埋めない
 */
export type ZigzagPractice =
  | { kind: "dojo"; slug: string; name: string; subtitle: string }
  | { kind: "project"; id: string; name: string; subtitle: string }
  | { kind: "general" };

export const ROLE_PRACTICE: Record<RoleId, ZigzagPractice> = {
  // 体験は崩れたボタンの直し。まず触る章の第11章 (モダンWeb) の React を、画面の直しで手を動かす
  frontend: { kind: "dojo", slug: "dojo-re", name: "React道場", subtitle: "ハレノヒ珈琲をモダン化する10問" },
  // 体験はお知らせの並びの直し (SQL)。まず触る章の 1 つ目が第6章 (データベース・SQL)
  backend: { kind: "dojo", slug: "dojo-sql", name: "SQL道場", subtitle: "図書館DBに挑む10問" },
  // まず触る章の 1 つ目が第4章 (Linux)。題材もサーバーの障害対応
  infrastructure: { kind: "dojo", slug: "dojo-lx", name: "Linux道場", subtitle: "商店街サーバー障害対応の10問" },
  // セキュリティの道場は無い。脆弱性の報告に対応する実践プロジェクト⑧が、仕事にいちばん近い
  security: {
    kind: "project",
    id: "security-quality-dx",
    name: "スミレクラウド |セキュリティ対応",
    subtitle: "実践プロジェクトで、|脆弱性の報告に対応します",
  },
  // 体験は売上の集計の直し。まず触る章の 1 つ目が第6章 (データベース・SQL)
  data: { kind: "dojo", slug: "dojo-sql", name: "SQL道場", subtitle: "図書館DBに挑む10問" },
  // 体験は AI の答えの直し。まず触る章の 1 つ目が第3章 (AI活用)
  ai: { kind: "dojo", slug: "dojo-pr", name: "プロンプト道場", subtitle: "商店街AI相談所の10問" },
  // 体験は画像の崩れの直し (CSS)。まず触る章の第11章の前半が HTML と CSS
  "web-production": { kind: "dojo", slug: "dojo-hc", name: "HTML/CSS道場", subtitle: "ハレノヒ珈琲を開店させる10問" },
  // まず触る章の 1 つ目が第3章 (AI活用)。仕事の頼み方をプロンプトで確かめる
  "ai-work": { kind: "dojo", slug: "dojo-pr", name: "プロンプト道場", subtitle: "商店街AI相談所の10問" },
};

export type ZigzagStep = {
  lane: ZigzagLane;
  /** 段の言葉。HP の 3 つの段と同じ順番 */
  verb: "基礎で分かる" | "実践で手を動かす" | "本物で使う";
  /** 使う物の名前。塊の途中では折らない。| は折ってよい所 */
  name: string;
  /** 名前の横に添える数 (基礎の項目の数だけ) */
  count?: { n: number; unit: string };
  /** 名前の下の 1 行。| は折ってよい所 */
  detail: string;
  /** 別に書いておくこと (開発演習の料金だけ) */
  note?: string;
};

const GENERAL_PRACTICE: Pick<ZigzagStep, "name" | "detail"> = {
  name: "道場と実践プロジェクト",
  detail: "分かったことを、|小さな課題で確かめます",
};

function practiceStep(role: RoleId): ZigzagStep {
  const p = ROLE_PRACTICE[role];
  if (p.kind === "general") return { lane: "prac", verb: "実践で手を動かす", ...GENERAL_PRACTICE };
  return {
    lane: "prac",
    verb: "実践で手を動かす",
    name: p.name,
    detail: p.subtitle,
  };
}

export function zigzagSteps(role: RoleId): ZigzagStep[] {
  const [first] = rolePathSummary(role).chapters;
  return [
    {
      lane: "base",
      verb: "基礎で分かる",
      name: `第${first.no}章 |${shortChapterName(first.title)}`,
      count: { n: first.items, unit: "項目" },
      detail: "学習パスの、|まず触る章",
    },
    practiceStep(role),
    {
      lane: "real",
      verb: "本物で使う",
      name: "実プロダクト開発演習",
      detail: "レベル1から少しずつ。|毎日使う学習サイトに機能を足し、|レビューを受けて本番に出します",
      note: "開発演習は、別途オプション料金です。",
    },
  ];
}
